// Copyright (c) Mysten Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import { useSuiClientQuery } from '@mysten/dapp-kit';
import { ArrowRight12 } from '@mysten/icons';
import { type SuiValidatorSummary } from '@mysten/sui.js/client';
import { Text } from '@mysten/ui';
import { useMemo } from 'react';

import { HighlightedTableCol } from "~/components/Table/HighlightedTableCol";
import { Banner } from "~/ui/Banner";
import { ImageIcon } from "~/ui/ImageIcon";
import { AddressLink, ValidatorLink } from "~/ui/InternalLink";
import { Link } from "~/ui/Link";
import { PlaceholderTable } from "~/ui/PlaceholderTable";
import { TableCard } from "~/ui/TableCard";
import { StakeColumn } from "./StakeColumn";
import { displayValidatorHeading } from '~/utils/displayValidator';
import { sortValidatorsByStake } from '~/utils/sortValidatorsByStake';

const NUMBER_OF_VALIDATORS = 10;

export function processValidators(set: SuiValidatorSummary[]) {
	return set.map((av) => ({
		name: displayValidatorHeading(av.name, av.description),
		address: av.suiAddress,
		stake: av.stakingPoolSuiBalance,
		logo: av.imageUrl,
	}));
}

const validatorsTable = (
	validatorsData: SuiValidatorSummary[],
	limit?: number,
	showIcon?: boolean,
) => {
	const validators = sortValidatorsByStake(validatorsData).map((av) => ({
		name: displayValidatorHeading(av.name, av.description),
		address: av.suiAddress,
		stake: av.stakingPoolSuiBalance,
		logo: av.imageUrl,
	}));

	const validatorsItems = limit ? validators.slice(0, limit) : validators;

	return {
    data: validatorsItems.map(({ name, stake, address, logo }) => ({
      name: (
        <HighlightedTableCol first>
          <div className="flex items-center gap-2.5">
            {showIcon && (
              <ImageIcon
                src={logo}
                size="sm"
                fallback={name}
                label={name}
                circle
              />
            )}

            <ValidatorLink address={address} label={name} />
          </div>
        </HighlightedTableCol>
      ),
      stake: <StakeColumn stake={stake} />,
      delegation: (
        <Text variant="bodySmall/medium" color="steel-darker">
          {stake.toString()}
        </Text>
      ),
      address: (
        <HighlightedTableCol>
          <AddressLink address={address} noTruncate={!limit} />
        </HighlightedTableCol>
      ),
    })),
    columns: [
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Address",
        accessorKey: "address",
      },
      {
        header: "Stake",
        accessorKey: "stake",
      },
    ],
  };
};

type TopValidatorsCardProps = {
	limit?: number;
	showIcon?: boolean;
};

export function TopValidatorsCard({ limit, showIcon }: TopValidatorsCardProps) {
	const { data, isPending, isSuccess, isError } = useSuiClientQuery('getLatestSuiSystemState');

	const tableData = useMemo(
		() => (data ? validatorsTable(data.activeValidators, limit, showIcon) : null),
		[data, limit, showIcon],
	);

	if (isError || (!isPending && !tableData?.data.length)) {
		return (
			<Banner variant="error" fullWidth>
				Validator data could not be loaded
			</Banner>
		);
	}

	return (
		<>
			{isPending && (
				<PlaceholderTable
					rowCount={limit || NUMBER_OF_VALIDATORS}
					rowHeight="13px"
					colHeadings={['Name', 'Address', 'Stake']}
					colWidths={['220px', '220px', '220px']}
				/>
			)}

			{isSuccess && tableData && (
				<div className="bg-[#081223] rounded-xl p-5 shadow-[0_0_50px_rgba(0,153,255,0.15)] border border-[#18233E]">
					<TableCard data={tableData.data} columns={tableData.columns} />
					<div className="mt-3 flex justify-between">
						<Link to={{ pathname: '/stake', hash: '#validators' }}>
							<div className="flex items-center gap-2 text-[#aeb9d0]">
								View all
								<ArrowRight12 fill="currentColor" className="h-3 w-3 -rotate-45" />
							</div>
						</Link>
						<Text variant="body/medium" color="dark">
							{data ? data.activeValidators.length : '-'}
							{` Total`}
						</Text>
					</div>
				</div>
			)}
		</>
	);
}
